var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React from 'react';
import PropTypes from 'prop-types';
import CollapsedAxis from './CollapsedAxis';
import YAxis from './YAxis';
import ScalerContext from '../../context/Scaler';
import GriffPropTypes, { seriesPropType, axisDisplayModeType } from '../../utils/proptypes';
import AxisDisplayMode from '../LineChart/AxisDisplayMode';
import AxisPlacement from '../AxisPlacement';

var propTypes = {
  height: PropTypes.number.isRequired,
  zoomable: PropTypes.bool,
  axisDisplayMode: axisDisplayModeType,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  yAxisPlacement: GriffPropTypes.axisPlacement,
  ticks: PropTypes.number,

  // Number => String
  tickFormatter: PropTypes.func,

  // These are populated by Griff.
  series: seriesPropType,
  collections: GriffPropTypes.collections,
  yAxisWidth: PropTypes.number
};

var defaultProps = {
  series: [],
  collections: [],
  zoomable: true,
  ticks: 5,
  yAxisWidth: 50,
  axisDisplayMode: AxisDisplayMode.ALL,
  yAxisPlacement: AxisPlacement.RIGHT,
  onMouseEnter: null,
  onMouseLeave: null,
  tickFormatter: Number
};

var onAxisMouseEnter = function onAxisMouseEnter(seriesId, _ref) {
  var onMouseEnter = _ref.onMouseEnter;
  return onMouseEnter ? function (e) {
    return onMouseEnter(e, seriesId);
  } : null;
};

var onAxisMouseLeave = function onAxisMouseLeave(seriesId, _ref2) {
  var onMouseLeave = _ref2.onMouseLeave;
  return onMouseLeave ? function (e) {
    return onMouseLeave(e, seriesId);
  } : null;
};

var axisFilter = function axisFilter(mode) {
  return function (s, _ref3) {
    var axisDisplayMode = _ref3.axisDisplayMode;
    return !s.hidden && (s.yAxisDisplayMode || axisDisplayMode) === mode;
  };
};

var ALL_FILTER = axisFilter(AxisDisplayMode.ALL);
var COLLAPSED_FILTER = axisFilter(AxisDisplayMode.COLLAPSED);

var placementFilter = function placementFilter(s, _ref4) {
  var yAxisPlacement = _ref4.yAxisPlacement;
  return !s.yAxisPlacement || s.yAxisPlacement === AxisPlacement.BOTH || s.yAxisPlacement === yAxisPlacement;
};

var getAxisOffsets = function getAxisOffsets(_ref5) {
  var axisDisplayMode = _ref5.axisDisplayMode,
      collections = _ref5.collections,
      series = _ref5.series,
      yAxisPlacement = _ref5.yAxisPlacement,
      yAxisWidth = _ref5.yAxisWidth;

  var numCollapsed = series.concat(collections).filter(function (item) {
    return COLLAPSED_FILTER(item, { axisDisplayMode: axisDisplayMode });
  }).length;
  var numVisible = series.reduce(function (count, s) {
    if (s.collectionId === undefined && ALL_FILTER(s, { axisDisplayMode: axisDisplayMode })) {
      return count + 1;
    }
    return count;
  }, 0) + collections.filter(function (c) {
    return ALL_FILTER(c, { axisDisplayMode: axisDisplayMode });
  }).length;

  switch (yAxisPlacement) {
    case AxisPlacement.LEFT:
      return {
        collapsed: 0,
        visible: numCollapsed ? yAxisWidth : 0
      };
    case AxisPlacement.BOTH:
      throw new Error('BOTH is not a valid option for AxisCollection -- please specify RIGHT or LEFT');
    case AxisPlacement.RIGHT:
    case AxisPlacement.UNSPECIFIED:
    default:
      return {
        collapsed: numVisible * yAxisWidth,
        visible: 0
      };
  }
};

var renderAllVisibleAxes = function renderAllVisibleAxes(offsetx, _ref6) {
  var axisDisplayMode = _ref6.axisDisplayMode,
      collections = _ref6.collections,
      height = _ref6.height,
      onMouseEnter = _ref6.onMouseEnter,
      onMouseLeave = _ref6.onMouseLeave,
      series = _ref6.series,
      tickFormatter = _ref6.tickFormatter,
      ticks = _ref6.ticks,
      yAxisPlacement = _ref6.yAxisPlacement,
      yAxisWidth = _ref6.yAxisWidth,
      zoomable = _ref6.zoomable;

  var axisOffsetX = offsetx - yAxisWidth;

  var filteredCollections = collections.filter(function (c) {
    return ALL_FILTER(c, { axisDisplayMode: axisDisplayMode });
  }).filter(function (c) {
    return placementFilter(c, { yAxisPlacement: yAxisPlacement });
  });
  if (yAxisPlacement === AxisPlacement.LEFT) {
    filteredCollections.reverse();
  }

  var filteredSeries = series.filter(function (s) {
    return ALL_FILTER(s, { axisDisplayMode: axisDisplayMode }) && placementFilter(s, { yAxisPlacement: yAxisPlacement }) && s.collectionId === undefined;
  });
  if (yAxisPlacement === AxisPlacement.LEFT) {
    filteredSeries.reverse();
  }

  return [].concat(filteredSeries.map(function (s) {
    axisOffsetX += yAxisWidth;
    return React.createElement(YAxis, {
      key: 'y-axis--' + s.id,
      offsetx: axisOffsetX,
      zoomable: s.zoomable !== undefined ? s.zoomable : zoomable,
      series: s,
      height: height,
      width: yAxisWidth,
      onMouseEnter: onAxisMouseEnter(s.id, { onMouseEnter: onMouseEnter }),
      onMouseLeave: onAxisMouseLeave(s.id, { onMouseLeave: onMouseLeave }),
      tickFormatter: tickFormatter,
      yAxisPlacement: yAxisPlacement,
      ticks: ticks
    });
  })).concat(filteredCollections.map(function (c) {
    axisOffsetX += yAxisWidth;
    return React.createElement(YAxis, {
      key: 'y-axis-collection-' + c.id,
      offsetx: axisOffsetX,
      zoomable: c.zoomable !== undefined ? c.zoomable : zoomable,
      collection: c,
      height: height,
      width: yAxisWidth,
      onMouseEnter: onAxisMouseEnter(c.id, { onMouseEnter: onMouseEnter }),
      onMouseLeave: onAxisMouseLeave(c.id, { onMouseLeave: onMouseLeave }),
      tickFormatter: tickFormatter,
      yAxisPlacement: yAxisPlacement,
      ticks: ticks
    });
  }));
};

var renderPlaceholderAxis = function renderPlaceholderAxis(offsetx, _ref7) {
  var axisDisplayMode = _ref7.axisDisplayMode,
      collections = _ref7.collections,
      height = _ref7.height,
      onMouseEnter = _ref7.onMouseEnter,
      onMouseLeave = _ref7.onMouseLeave,
      series = _ref7.series,
      yAxisPlacement = _ref7.yAxisPlacement,
      yAxisWidth = _ref7.yAxisWidth;

  var collapsed = series.filter(function (s) {
    return placementFilter(s, { yAxisPlacement: yAxisPlacement });
  }).concat(collections).filter(function (item) {
    return COLLAPSED_FILTER(item, { axisDisplayMode: axisDisplayMode });
  });
  // TODO: Should we only do this if there's more than 1?
  if (collapsed.length) {
    return React.createElement(CollapsedAxis, {
      key: 'y-axis--collapsed',
      height: height,
      offsetx: offsetx,
      width: yAxisWidth,
      onMouseEnter: onAxisMouseEnter('collapsed', { onMouseEnter: onMouseEnter }),
      onMouseLeave: onAxisMouseLeave('collapsed', { onMouseLeave: onMouseLeave }),
      yAxisPlacement: yAxisPlacement
    });
  }
  return null;
};

var AxisCollection = function AxisCollection(_ref8) {
  var axisDisplayMode = _ref8.axisDisplayMode,
      collections = _ref8.collections,
      height = _ref8.height,
      onMouseEnter = _ref8.onMouseEnter,
      onMouseLeave = _ref8.onMouseLeave,
      series = _ref8.series,
      tickFormatter = _ref8.tickFormatter,
      ticks = _ref8.ticks,
      yAxisPlacement = _ref8.yAxisPlacement,
      yAxisWidth = _ref8.yAxisWidth,
      zoomable = _ref8.zoomable;

  var calculatedWidth = [].concat(series).concat(collections).filter(function (item) {
    return item.collectionId === undefined;
  }).filter(function (item) {
    return ALL_FILTER(item, { axisDisplayMode: axisDisplayMode });
  }).filter(function (item) {
    return placementFilter(item, { yAxisPlacement: yAxisPlacement });
  }).reduce(function (acc, item) {
    if (item.yAxisDisplayMode === AxisDisplayMode.COLLAPSED) {
      return acc;
    }
    return acc + yAxisWidth;
  }, series.filter(function (s) {
    return COLLAPSED_FILTER(s, { axisDisplayMode: axisDisplayMode });
  }).length ? yAxisWidth : 0);

  var _getAxisOffsets = getAxisOffsets({
    axisDisplayMode: axisDisplayMode,
    collections: collections,
    series: series,
    yAxisPlacement: yAxisPlacement,
    yAxisWidth: yAxisWidth
  }),
      collapsedOffsetX = _getAxisOffsets.collapsed,
      visibleOffsetX = _getAxisOffsets.visible;

  // We need to render all of the axes (even if they're hidden) in order to
  // keep the zoom states in sync across show/hide toggles.


  var axes = renderAllVisibleAxes(visibleOffsetX, {
    axisDisplayMode: axisDisplayMode,
    collections: collections,
    height: height,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    series: series,
    tickFormatter: tickFormatter,
    ticks: ticks,
    yAxisPlacement: yAxisPlacement,
    yAxisWidth: yAxisWidth,
    zoomable: zoomable
  });
  return React.createElement(
    'svg',
    { width: calculatedWidth, height: height },
    axes,
    renderPlaceholderAxis(collapsedOffsetX, {
      axisDisplayMode: axisDisplayMode,
      collections: collections,
      height: height,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      series: series,
      yAxisPlacement: yAxisPlacement,
      yAxisWidth: yAxisWidth
    })
  );
};
AxisCollection.propTypes = process.env.NODE_ENV !== "production" ? propTypes : {};
AxisCollection.defaultProps = defaultProps;

export default (function (props) {
  return React.createElement(
    ScalerContext.Consumer,
    null,
    function (_ref9) {
      var collections = _ref9.collections,
          series = _ref9.series;
      return React.createElement(AxisCollection, _extends({}, props, { collections: collections, series: series }));
    }
  );
});